// This code is intentionally NOT exported by our root index.
// It can be imported and used but as long as we don't export it,
// Any code that uses it and is tree-shaken will also try and
// tree-shake this as well.
// The goal here is to ensure as many translations as possible can
// be tree-shaken if the components that used them are also tree-shaken.
import { setupI18n } from "@lingui/core";
import type { I18n } from "@lingui/core";
import { warnOnce } from "@smartsheet/lodestar-utils";
import { de, en, es, fr, it, ja, pt, ru } from "make-plural/plurals";
import React, { createContext, FC, forwardRef, useContext, useMemo } from "react";
import { ComponentType } from "react";
import { SupportedLocales } from "./constants";

export const allLocales = ["de", "en", "es", "fr", "it", "ja", "pt", "ru"];

const initializeI18n = () =>
    setupI18n({
        locale: "en",
        locales: allLocales,
        localeData: {
            de: { plurals: de },
            en: { plurals: en },
            es: { plurals: es },
            fr: { plurals: fr },
            it: { plurals: it },
            ja: { plurals: ja },
            pt: { plurals: pt },
            ru: { plurals: ru },
        },
        messages: {
            de: {},
            en: {},
            es: {},
            fr: {},
            it: {},
            ja: {},
            pt: {},
            ru: {},
        },
    });

/**
 * Singleton instance of lodestar-core's i18n object.
 * All lodestar-core translations should be installed into this bundle.
 */
let i18n = initializeI18n();

export const getI18n = () => i18n;

// PLEASE ONLY USE IN TEST! I HATE MYSELF FOR THIS.
export const clearForTest = () => {
    i18n = initializeI18n();
};

export interface I18nProviderProps {
    // TODO this should be required and of type SupportedLocales in a future major release.
    /**
     * Locale to use for translations.
     */
    locale?: string;
}

export interface I18nProviderPropsStrict {
    // TODO this should be required and of type SupportedLocales in a future major release.
    /**
     * Locale to use for translations.
     */
    locale: SupportedLocales;
}

export interface WithI18nInjectedProps {
    /**
     * Locale to use for translations.
     */
    i18n: I18n;
}

interface I18NContextValue {
    i18n: I18n;
}

// This default impl is just a marker to say the context has not been initialized
const defaultI18nValue = {
    i18n: "uninitialized-marker",
} as unknown as I18NContextValue;
const I18nContext = createContext<I18NContextValue>(defaultI18nValue);

const warnWhenNoParentCtx = warnOnce(
    "You've called `useI18n` without a <I18nContextProvider> above it. This will result in errors, as the provider constructs the i18n instance."
);

/**
 * Hook to access the i18n value.
 */
export const useI18n = (): I18NContextValue => {
    const ctx = useContext(I18nContext);

    if (process.env.NODE_ENV !== "production") {
        if (ctx === defaultI18nValue) {
            warnWhenNoParentCtx();
        }
    }

    return ctx;
};

const warnWhenNoLocaleAtRootProvider = warnOnce(
    "No locale found for the top-most I18nProvider." +
        " The top-most I18nProvider owns the i18n instance for all" +
        " sub-I18nProviders and needs a locale to function correctly."
);

const warnWhenMismatchedLocaleAtSubProvider = warnOnce(
    "The locale passed to a sub-provider didn't match the locale found for the" +
        " top-most I18nProvider." +
        " The top-most I18nProvider owns the i18n instance for all" +
        " sub-I18nProviders and needs a locale to function correctly." +
        " Either omit the locale from the sub-providers, or match the parent."
);

const supportedLocaleSet = new Set(allLocales);

/**
 * Memoizing, and proxying Context provider for an i18n instance.
 * This can be used multiple times and will always read from the highest instance.
 * This is to reduce the number of i18n objects we have lying around.
 */
export const I18nContextProvider: FC<I18nProviderProps> = ({ children, locale }) => {
    const parentCtx = useContext(I18nContext);
    const isRootCtx = parentCtx === defaultI18nValue;

    // We're going to get a completely new object here to ensure the children of every
    // provider will re-render if the locale changes.
    const parentOrNewI18n = useMemo(() => {
        let newCtx: WithI18nInjectedProps;
        if (isRootCtx) {
            // If there's no locale passed to the root provider, we have a problem that
            // will manifest in errors in lingui.
            if (process.env.NODE_ENV !== "production") {
                if (locale === undefined) {
                    warnWhenNoLocaleAtRootProvider();
                } else if (!supportedLocaleSet.has(locale)) {
                    console.warn(
                        `lodestar-core components: locale "${locale}" not recognized. ` +
                            `This will result in improper translations. Please pass one ` +
                            `of [${allLocales.join(", ")}]`
                    );
                }
            }
            // Make sure it works if the locale isn't passed in for some reason.
            // TODO will lingui default this for us?
            const localeToUse = locale !== undefined ? locale : "en";
            newCtx = {
                i18n,
            };
            if (i18n.locale !== locale) {
                // TODO is this side-effect too dangerous to do within the react render?
                i18n.activate(localeToUse);
            }
        } else {
            newCtx = parentCtx;
            // If the locale passed to a sub-provider isn't the same as the root provider,
            // we have a problem that will probably result in different translations across renders.
            if (process.env.NODE_ENV !== "production") {
                if (locale !== undefined && locale !== newCtx.i18n.locale) {
                    warnWhenMismatchedLocaleAtSubProvider();
                }
            }
        }

        return newCtx;
    }, [isRootCtx, parentCtx, locale]);

    return <I18nContext.Provider value={parentOrNewI18n}>{children}</I18nContext.Provider>;
};

interface I18NConsumerProps {
    children: ({ i18n }: { i18n: I18n }) => JSX.Element;
}

export const I18NContextConsumer: FC<I18NConsumerProps> = ({ children }) => {
    return children(useI18n());
};

/**
 * This hook is our go-to, heavy-lifter for adding i18n to a component. It:
 * * injects the catalog
 * * wraps the component in the provider
 *
 * From here, all the internal component/subcomponents need to do is use the `useI18n()` hook to access the i18n object translations.
 */
export const withI18nProvider = <Props, E extends HTMLElement = HTMLElement>(
    /**
     * The component to wrap with our I18nProvider.
     */
    WrappedComponent: ComponentType<Props>,

    /**
     * A function that should inject the translations to the shared i18n instance.
     * This should be wrapped w/ util/once to ensure it's not called multiple times.
     */
    injectTranslations: () => void
) => {
    const WithI18nProvider = forwardRef<E, Props & I18nProviderProps>(({ locale, ...props }, ref) => {
        injectTranslations();
        return (
            <I18nContextProvider locale={locale}>
                <WrappedComponent {...(props as Props)} ref={ref} />
            </I18nContextProvider>
        );
    });

    const displayName = WrappedComponent.displayName || WrappedComponent.name || "Component";
    WithI18nProvider.displayName = `withI18nProvider(${displayName})`;

    return WithI18nProvider;
};
